import { useState, useEffect, createRef, useCallback } from 'react';

export interface DrawerDialogProps {
  id?: string;
  openDialog?: boolean;
  closeDialog?: () => void;
  children?: JSX.Element;
  dialogStatic?: boolean;
  triggerLabel?: string;
  dialogHeading?: string;
  dialogTriggerSecondary?: boolean;
  dialogTriggerClass?: string;
  dialogLarge?: boolean;
  hideFooter?: boolean;
  okButtonLabel?: string; // Custom label for the Ok button
  closeButtonLabel?: string; // Custom label for the Close button
  showOnlyCloseButton?: boolean; // Flag to show only the Close button
  useLinkTrigger?: boolean;
  customSaveAction?: () => void;
  customCloseAction?: () => void;
}

export const DrawerDialog = ({
  id,
  openDialog,
  closeDialog,
  children,
  dialogStatic,
  triggerLabel,
  dialogHeading,
  dialogTriggerSecondary,
  dialogTriggerClass,
  dialogLarge,
  hideFooter,
  okButtonLabel = 'Ok', // Default label "Ok"
  closeButtonLabel = 'Close', // Default label "Close"
  showOnlyCloseButton = false, // Default to showing both buttons
  useLinkTrigger,
  customSaveAction,
  customCloseAction,
}: DrawerDialogProps) => {

  // IMPORTANT: DO NOT USE THIS DRAWER ANYMORE
  // Recommend use of OrbitDrawer instead

  const defaultDialogId =
    id || `drawer-dialog-${Math.random().toString(36).substr(2, 9)}`;
  const [thisDialog, setThisDialog] = useState(Boolean(openDialog));

  const handleChange = useCallback(
    (open: boolean) => {
      if (closeDialog) {
        closeDialog();
      }
      setThisDialog(open);
    },
    [closeDialog]
  );

  useEffect(() => {
    if (openDialog) {
      setThisDialog(openDialog);
    }
  }, [openDialog]);

  const dialogContentClasses = dialogLarge
    ? 'drawer-content size-large'
    : 'drawer-content';
  const triggerLevel = dialogTriggerSecondary ? 'btn btn-secondary' : 'btn';

  const innerTrigger = useLinkTrigger ? (
    <a
      href="#!"
      onClick={(e) => {
        e.preventDefault();
        setThisDialog(true);
      }}
      role="button"
    >
      {triggerLabel}
    </a>
  ) : (
    <button
      id={`${defaultDialogId}-trigger`}
      className={`${triggerLevel} ${dialogTriggerClass || ''} py-0`}
      onClick={() => setThisDialog(true)}
    >
      {triggerLabel}
    </button>
  );

  let footer = null;

  if (!hideFooter) {
    footer = (
      <footer className="drawer-row">
        <button
          className="btn mr-4"
          onClick={() => {
            handleChange(false);
            if (customCloseAction) {
              customCloseAction();
            }
          }}
          id={`${defaultDialogId}-close-btn`}
        >
          {closeButtonLabel}
        </button>
        {!showOnlyCloseButton && (
          <button
            className="btn btn-primary"
            onClick={() => {
              handleChange(false);
              if (customSaveAction) {
                customSaveAction();
              }
            }}
            id={`${defaultDialogId}-primary-btn`}
          >
            {okButtonLabel}
          </button>
        )}
      </footer>
    );
  }

  const ref = createRef<HTMLDialogElement>();

  useEffect(() => {
    const drawerHtmlTag = document.querySelector('html');
    const dialog = ref.current;

    const openAction = () => {
      dialog?.showModal();

      if (
        window.getComputedStyle(dialog as Element, null).display === 'block'
      ) {
        ref.current?.classList.add('dialog-shade');
      }

      drawerHtmlTag?.classList.add('drawer-open');
    };

    const closeAction = () => {
      // if (dialog?.classList.contains('drawer-dialog')) dialog.classList.remove('dialog-shade')

      // stopPropagation caused by any css transitions by drawer children
      // additional elements with css transitions inside the drawer should be placed in 'querySelectorAll`
      dialog
        ?.querySelectorAll('button, a')
        .forEach((item) =>
          item.addEventListener('transitionend', (e) => e.stopPropagation())
        );

      // close dialog on drawer transitionend - remove 'open' attr on <dialog> tag
      dialog?.addEventListener('transitionend', (e) => {
        if (dialog.open && !dialog.classList.contains('dialog-shade')) {
          if (e.propertyName !== 'right') {
            dialog?.close();
            drawerHtmlTag?.classList.remove('drawer-open');
          }
        }
      });
    };

    // Click Backdrop to Close
    document.querySelector(`dialog#${id}`)?.addEventListener('click', (e) => {
      const target = e.target as HTMLDialogElement;
      const isBackdrop = target.classList.contains('dialog-shade');

      if (dialogStatic && isBackdrop) {
        return bounceDrawer(target);
      }

      if (target.classList.contains('drawer-dialog')) {
        dialog?.classList.remove('dialog-shade');
      }

      if (!dialogStatic && isBackdrop) {
        if (closeDialog) {
          handleChange(false);
        } else {
          setThisDialog(false);
        }
      }
    });

    const bounceDrawer = (target: HTMLDialogElement) => {
      const drawerDialogOpen = target.classList.contains('dialog-shade');

      if (drawerDialogOpen) {
        dialog?.classList.add('dialog-static');
        setTimeout(() => {
          dialog?.classList.remove('dialog-static');
        }, 175);
      }
    };

    if (thisDialog) {
      openAction();
    } else {
      if (dialog?.classList.contains('drawer-dialog')) {
        dialog.classList.remove('dialog-shade');
      }

      closeAction();
    }
  }, [thisDialog, ref, id, dialogStatic, closeDialog, handleChange]);
  // end useEffect()

  useEffect(() => {
    const focusableElements =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const dialogNode = ref.current;
    if (dialogNode) {
      const focusableContent = dialogNode.querySelectorAll(focusableElements);
      const firstFocusableElement = focusableContent[0] as HTMLElement; // Cast to HTMLElement
      const lastFocusableElement = focusableContent[
        focusableContent.length - 1
      ] as HTMLElement; // Cast to HTMLElement

      const trapFocus = (e: KeyboardEvent) => {
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        if (!isTabPressed) {
          return;
        }

        if (e.shiftKey) {
          // if shift key pressed for shift + tab combination
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus(); // add focus for the last focusable element
            e.preventDefault();
          }
        } else {
          // if tab key is pressed
          if (document.activeElement === lastFocusableElement) {
            // if focused has reached to last focusable element then focus first focusable element after pressing tab
            firstFocusableElement.focus(); // add focus for the first focusable element
            e.preventDefault();
          }
        }
      };

      if (thisDialog) {
        firstFocusableElement.focus();
        document.addEventListener('keydown', trapFocus);
      }

      return () => {
        document.removeEventListener('keydown', trapFocus);
      };
    }
  }, [thisDialog, ref]);

  return (
    <>
      {triggerLabel && innerTrigger}
      <dialog
        className={`drawer-dialog ${dialogLarge ? 'size-large' : ''}`}
        ref={ref}
        onCancel={() => handleChange(false)}
        id={defaultDialogId}
      >
        <div className={dialogContentClasses}>
          <div className="drawer-item first-item">
            <button
              className="drawer-close"
              other-data="data-close-button"
              onClick={() => {
                handleChange(false);
                if (customCloseAction) {
                  customCloseAction();
                }
              }}
              tabIndex={1}
            >
              <i className="ph ph-x" aria-hidden="true" role="presentation" />
              <span className="sr-only">Close</span>
            </button>
          </div>
          <div className="drawer-item">
            <div className="drawer-row">
              {dialogHeading && ( // Conditional rendering based on dialogHeading
                <header className="drawer-header">
                  <h1>{dialogHeading}</h1>
                </header>
              )}
              <div className="drawer-main">{children}</div>
            </div>
            {footer}
          </div>
        </div>
      </dialog>
    </>
  );
};
